// src/portal-app/insights/rangeState.js
//
// W2 final review M1 — ONE shared range preset for every Insights surface.
//
// Before this, each surface owned useState('week'): a manager working in
// "This Month" had to reselect it on every surface, and again after every
// back-navigation (popping unmounts the screen). W3 adds four more
// range-bearing surfaces, so the preset is lifted here once.
//
// Why a React context (and not sessionStorage): the provider mounts in
// PortalShell, so the preset survives pops and tab switches (the provider
// outlives every pushed screen) and dies with the shell on sign-out —
// nothing persists across accounts, no storage cleanup wired into auth.
// sessionStorage would outlive sign-out inside the same browser session.
//
// The setter validates against the Task-1 PRESETS registry: a junk id (a
// stale persisted value, a typo'd deep link) is coerced to the default, so
// rangeForPreset(preset) can never throw off this state.
//
// useRangePreset() degrades to plain local state when no provider is
// mounted (isolated unit mounts, storybook-style harnesses) — same
// [preset, setPreset] tuple either way.
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { PRESETS } from '../nav/dateRange';

export const DEFAULT_PRESET = 'week';
const VALID_IDS = new Set(PRESETS.map((p) => p.id));
const coerce = (id) => (VALID_IDS.has(id) ? id : DEFAULT_PRESET);

const RangePresetContext = createContext(null);

export function RangePresetProvider({ children }) {
    const [preset, setRaw] = useState(DEFAULT_PRESET);
    const setPreset = useCallback((id) => setRaw(coerce(id)), []);
    const value = useMemo(() => ({ preset, setPreset }), [preset, setPreset]);
    return (
        <RangePresetContext.Provider value={value}>
            {children}
        </RangePresetContext.Provider>
    );
}

/** @returns {[string, (id: string) => void]} the shared [preset, setPreset] */
export function useRangePreset() {
    const ctx = useContext(RangePresetContext);
    // Local fallback — the hook must be unconditional (rules of hooks), the
    // context value simply wins when a provider is present.
    const [localPreset, setLocalRaw] = useState(DEFAULT_PRESET);
    const setLocalPreset = useCallback((id) => setLocalRaw(coerce(id)), []);
    if (ctx) return [ctx.preset, ctx.setPreset];
    return [localPreset, setLocalPreset];
}
